/**
 * IndexedDB-backed draft store for document web editing.
 *
 * Drafts are local-only and survive reload. They contain the editor blocks plus
 * the rest of the document-machine draft payload, keyed by `draftId`. A secondary
 * index on `docId` lets us look up the latest draft for a document on mount.
 */

import type {HMBlockNode, HMMetadata, HMNavigationItem, HMResourceVisibility} from '@seed-hypermedia/client/hm-types'
import {deriveDocumentType} from '@shm/shared/models/document-machine'
import {hmId, unpackHmId} from '@shm/shared/utils/entity-id-url'
import type {HMListedDraftWithLocation} from '@shm/shared/draft-breadcrumb-context'

export interface WebDocDraft {
  /** Primary key — stable id assigned on first save. */
  draftId: string
  /** Document id (`docId.id` form) this draft targets. Used for the docId index. */
  docId: string
  /** Vault-delegated account UID that will sign the publish. */
  signingAccountId: string
  /** Capability CID used by non-owner publishes. */
  capabilityCid?: string
  /** Editor blocks at the time of last save. */
  content: HMBlockNode[]
  /** Published content used as the three-way rebase baseline; absent in older drafts. */
  baseBlocks?: HMBlockNode[] | null
  /** Locally touched block IDs to restore for conflict detection. */
  mineTouchedIds?: string[]
  /** Explicit authored child-reference removals; cleared with the draft. */
  removedChildDocumentIds?: string[]
  /** Baselines invalidated by maintenance; stale autosaves are retained as recovery snapshots instead. */
  maintenancePreviousDeps?: string[][]
  /** Local maintenance epoch, independent of the published version. */
  maintenanceRevision?: number
  /** Pending metadata changes for this draft (subset of HMMetadata). */
  metadata: HMMetadata | Record<string, unknown>
  /** Heads we will use as `baseVersion` on publish. */
  deps: string[]
  /** Pending navigation override, or null if untouched. */
  navigation: HMNavigationItem[] | null
  /** Location uid for path moves; null when not moving. */
  locationUid: string | null
  /** Location path for moves. */
  locationPath: string[] | null
  /** Edit uid (target account when editing through a capability). */
  editUid: string | null
  /** Edit path under that account. */
  editPath: string[] | null
  /** Draft document visibility. Defaults to public for older records. */
  visibility?: HMResourceVisibility
  /** Cursor offset to restore on re-entering editing. */
  cursorPosition: number | null
  /** Optional path the user picked via the publish-popover rename affordance. */
  publishPath?: string[] | null
  /** The working schema of a draft whose document defines a type (not metadata). */
  schemaDraft?: Record<string, any> | null
  /** The working attributes schemas behind the binding keys, by key (not metadata). */
  bindingSchemaDrafts?: Record<string, Record<string, any>> | null
  /** Wall-clock timestamp of last save. Used for cleanup. */
  updatedAt: number
  /** Type derived from the persisted draft content. */
  isCollection?: boolean
}

const DB_NAME = 'web-doc-drafts-01'
const DB_VERSION = 2
const STORE = 'drafts-01'
const SNAPSHOT_STORE = 'draft-snapshots-01'
const INDEX_DOC = 'docId'
const INDEX_UPDATED = 'updatedAt'
const INDEX_DRAFT_ID = 'draftId'

const DEFAULT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000 // 30 days
const MAX_SNAPSHOTS_PER_DRAFT = 20

export interface WebDocDraftSnapshot {
  /** Primary key for this recoverable snapshot. */
  snapshotId: string
  /** Draft id this snapshot can restore. */
  draftId: string
  /** Wall-clock timestamp when the snapshot was created. */
  createdAt: number
  /** Full draft record before it was overwritten or deleted. */
  draft: WebDocDraft
}

let dbPromise: Promise<IDBDatabase> | null = null

function isBrowser(): boolean {
  return typeof indexedDB !== 'undefined'
}

function openDB(): Promise<IDBDatabase> {
  if (!isBrowser()) return Promise.reject(new Error('IndexedDB unavailable'))
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, {keyPath: 'draftId'})
        store.createIndex(INDEX_DOC, 'docId', {unique: false})
        store.createIndex(INDEX_UPDATED, 'updatedAt', {unique: false})
      }
      if (!db.objectStoreNames.contains(SNAPSHOT_STORE)) {
        const store = db.createObjectStore(SNAPSHOT_STORE, {keyPath: 'snapshotId'})
        store.createIndex(INDEX_DRAFT_ID, 'draftId', {unique: false})
        store.createIndex(INDEX_UPDATED, 'createdAt', {unique: false})
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbPromise
}

function tx(mode: IDBTransactionMode, storeName: string = STORE): Promise<IDBObjectStore> {
  return openDB().then((db) => db.transaction(storeName, mode).objectStore(storeName))
}

function reqToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

/** Read a draft by id. Returns null on miss or in non-browser environments. */
export async function getWebDocDraft(draftId: string): Promise<WebDocDraft | null> {
  if (!isBrowser()) return null
  try {
    const store = await tx('readonly')
    const result = await reqToPromise<WebDocDraft | undefined>(store.get(draftId))
    return result ?? null
  } catch (err) {
    console.warn('getWebDocDraft failed', err)
    return null
  }
}

/** Write or replace a draft. Stamps `updatedAt`. */
export async function putWebDocDraft(draft: Omit<WebDocDraft, 'updatedAt'> & {updatedAt?: number}): Promise<void> {
  if (!isBrowser()) return
  const documentId = unpackHmId(draft.docId) ?? hmId(draft.editUid ?? draft.locationUid ?? '')
  const record: WebDocDraft = {
    ...draft,
    isCollection: deriveDocumentType(draft.content, documentId) === 'collection',
    updatedAt: draft.updatedAt ?? Date.now(),
  }
  const db = await openDB()
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction([STORE, SNAPSHOT_STORE], 'readwrite')
    const drafts = transaction.objectStore(STORE)
    let stale = false
    transaction.oncomplete = () =>
      stale ? reject(new Error('Draft baseline changed; reload to reconcile your edits')) : resolve()
    transaction.onerror = () => reject(transaction.error ?? new Error('Draft write failed'))
    transaction.onabort = () => reject(transaction.error ?? new Error('Draft write aborted'))
    const request = drafts.get(record.draftId)
    request.onsuccess = () => {
      const existing = request.result as WebDocDraft | undefined
      const previousDeps = existing?.maintenancePreviousDeps ?? []
      stale =
        (record.maintenanceRevision ?? 0) !== (existing?.maintenanceRevision ?? 0) ||
        previousDeps.some(
          (deps) => deps.length === record.deps.length && deps.every((dep) => record.deps.includes(dep)),
        )
      const snapshotDraft = stale ? record : existing
      if (snapshotDraft) {
        const createdAt = Date.now()
        transaction.objectStore(SNAPSHOT_STORE).put({
          snapshotId: `${record.draftId}:${createdAt}:${Math.random().toString(36).slice(2)}`,
          draftId: record.draftId,
          createdAt,
          draft: snapshotDraft,
        } satisfies WebDocDraftSnapshot)
      }
      if (!stale) drafts.put({...record, maintenancePreviousDeps: previousDeps})
    }
  })
  await pruneDraftSnapshots(record.draftId)
}

/** Atomically rebases an unchanged draft, retaining the previous record for recovery. */
export async function compareAndSwapWebDocDraft(expected: WebDocDraft, replacement: WebDocDraft): Promise<boolean> {
  if (expected.draftId !== replacement.draftId) throw new Error('Cannot change draft identity during reconciliation')
  const db = await openDB()
  const documentId = unpackHmId(replacement.docId) ?? hmId(replacement.editUid ?? replacement.locationUid ?? '')
  const record: WebDocDraft = {
    ...replacement,
    isCollection: deriveDocumentType(replacement.content, documentId) === 'collection',
    updatedAt: Date.now(),
  }
  return new Promise<boolean>((resolve, reject) => {
    const transaction = db.transaction([STORE, SNAPSHOT_STORE], 'readwrite')
    const drafts = transaction.objectStore(STORE)
    let replaced = false
    transaction.oncomplete = () => resolve(replaced)
    transaction.onerror = () => reject(transaction.error ?? new Error('Draft reconciliation transaction failed'))
    transaction.onabort = () => reject(transaction.error ?? new Error('Draft reconciliation transaction aborted'))
    const request = drafts.get(expected.draftId)
    request.onsuccess = () => {
      const current = request.result as WebDocDraft | undefined
      if (!current || JSON.stringify(current) !== JSON.stringify(expected)) return
      const createdAt = Date.now()
      transaction.objectStore(SNAPSHOT_STORE).put({
        snapshotId: `${current.draftId}:${createdAt}:${Math.random().toString(36).slice(2)}`,
        draftId: current.draftId,
        createdAt,
        draft: current,
      } satisfies WebDocDraftSnapshot)
      const previousDeps = current.maintenancePreviousDeps ?? []
      const changedBaseline =
        current.deps.length !== record.deps.length || current.deps.some((dep) => !record.deps.includes(dep))
      drafts.put({
        ...record,
        maintenanceRevision: (current.maintenanceRevision ?? 0) + 1,
        maintenancePreviousDeps: changedBaseline ? [...previousDeps, current.deps] : previousDeps,
      })
      replaced = true
    }
  })
}

/** Delete a draft by id. Idempotent. */
export async function deleteWebDocDraft(draftId: string): Promise<void> {
  if (!isBrowser()) return
  const existing = await getWebDocDraft(draftId)
  if (existing) await saveDraftSnapshot(existing)
  const store = await tx('readwrite')
  await reqToPromise(store.delete(draftId))
}

async function saveDraftSnapshot(draft: WebDocDraft): Promise<void> {
  const createdAt = Date.now()
  const snapshot: WebDocDraftSnapshot = {
    snapshotId: `${draft.draftId}:${createdAt}:${Math.random().toString(36).slice(2)}`,
    draftId: draft.draftId,
    createdAt,
    draft,
  }
  const store = await tx('readwrite', SNAPSHOT_STORE)
  await reqToPromise(store.put(snapshot))
  await pruneDraftSnapshots(draft.draftId)
}

async function pruneDraftSnapshots(draftId: string): Promise<void> {
  const snapshots = await listWebDocDraftSnapshots(draftId)
  const old = snapshots.slice(MAX_SNAPSHOTS_PER_DRAFT)
  if (!old.length) return
  const store = await tx('readwrite', SNAPSHOT_STORE)
  for (const snapshot of old) {
    await reqToPromise(store.delete(snapshot.snapshotId))
  }
}

/** Return recoverable snapshots for a draft, newest first. */
export async function listWebDocDraftSnapshots(draftId: string): Promise<WebDocDraftSnapshot[]> {
  if (!isBrowser()) return []
  try {
    const store = await tx('readonly', SNAPSHOT_STORE)
    const index = store.index(INDEX_DRAFT_ID)
    const results = await reqToPromise<WebDocDraftSnapshot[]>(index.getAll(draftId))
    return results.sort((a, b) => b.createdAt - a.createdAt)
  } catch (err) {
    console.warn('listWebDocDraftSnapshots failed', err)
    return []
  }
}

/** Restore a recoverable draft snapshot by writing it back to the active draft store. */
export async function restoreWebDocDraftSnapshot(snapshotId: string): Promise<void> {
  if (!isBrowser()) return
  const store = await tx('readonly', SNAPSHOT_STORE)
  const snapshot = await reqToPromise<WebDocDraftSnapshot | undefined>(store.get(snapshotId))
  if (!snapshot) throw new Error(`web draft snapshot ${snapshotId} not found`)
  await putWebDocDraft({...snapshot.draft, updatedAt: Date.now()})
}

/**
 * Return all drafts located under `parentLocationUid` + `parentLocationPath`,
 * newest first. Used by the query-block draft slot to render inline child
 * draft cards under the query target.
 */
export async function listWebDocChildDrafts(
  parentLocationUid: string,
  parentLocationPath: string[],
): Promise<WebDocDraft[]> {
  if (!isBrowser()) return []
  try {
    const store = await tx('readonly')
    const all = await reqToPromise<WebDocDraft[]>(store.getAll())
    return all
      .filter(
        (draft) => draft.locationUid === parentLocationUid && pathEquals(draft.locationPath ?? [], parentLocationPath),
      )
      .sort((a, b) => b.updatedAt - a.updatedAt)
  } catch (err) {
    console.warn('listWebDocChildDrafts failed', err)
    return []
  }
}

function pathEquals(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

/**
 * Adapt the stored web draft record to the shared `HMListedDraftWithLocation`
 * shape so providers can hand drafts to shared editor + query-block UIs that
 * already speak the desktop draft list shape.
 */
export function webDraftToListedDraft(draft: WebDocDraft): HMListedDraftWithLocation {
  const locationUid = draft.locationUid ?? undefined
  const editUid = draft.editUid ?? undefined
  const locationPath = draft.locationPath ?? undefined
  const editPath = draft.editPath ?? undefined
  return {
    id: draft.draftId,
    locationUid,
    locationPath,
    editUid,
    editPath,
    metadata: (draft.metadata ?? {}) as HMMetadata,
    visibility: draft.visibility ?? 'PUBLIC',
    deps: draft.deps,
    navigation: draft.navigation ?? undefined,
    lastUpdateTime: draft.updatedAt,
    isCollection: draft.isCollection ?? false,
    locationId: locationUid ? hmId(locationUid, {path: locationPath ?? []}) : undefined,
    editId: editUid ? hmId(editUid, {path: editPath ?? []}) : undefined,
  } as HMListedDraftWithLocation
}

/** Return all drafts for a given account uid, newest first. */
export async function listWebDocDraftsForAccount(accountUid: string | undefined): Promise<WebDocDraft[]> {
  if (!accountUid || !isBrowser()) return []
  try {
    const store = await tx('readonly')
    const all = await reqToPromise<WebDocDraft[]>(store.getAll())
    return all
      .filter((draft) => draft.locationUid === accountUid || draft.editUid === accountUid)
      .sort((a, b) => b.updatedAt - a.updatedAt)
  } catch (err) {
    console.warn('listWebDocDraftsForAccount failed', err)
    return []
  }
}

/** Return all drafts for a given docId, newest first. */
export async function listWebDocDraftsForDoc(docId: string): Promise<WebDocDraft[]> {
  if (!isBrowser()) return []
  try {
    const store = await tx('readonly')
    const index = store.index(INDEX_DOC)
    const results = await reqToPromise<WebDocDraft[]>(index.getAll(docId))
    return results.sort((a, b) => b.updatedAt - a.updatedAt)
  } catch (err) {
    console.warn('listWebDocDraftsForDoc failed', err)
    return []
  }
}

/** Return the latest draft for a docId, or null. */
export async function getLatestWebDocDraftForDoc(docId: string): Promise<WebDocDraft | null> {
  const drafts = await listWebDocDraftsForDoc(docId)
  return drafts[0] ?? null
}

/** Delete drafts older than `maxAgeMs` (default 30 days). */
export async function cleanupOldWebDocDrafts(maxAgeMs: number = DEFAULT_MAX_AGE_MS): Promise<number> {
  if (!isBrowser()) return 0
  try {
    const cutoff = Date.now() - maxAgeMs
    const store = await tx('readwrite')
    const index = store.index(INDEX_UPDATED)
    const range = IDBKeyRange.upperBound(cutoff)
    const oldKeys = await reqToPromise<IDBValidKey[]>(index.getAllKeys(range))
    let deleted = 0
    for (const key of oldKeys) {
      if (typeof key !== 'string') continue
      await deleteWebDocDraft(key)
      deleted += 1
    }
    return deleted
  } catch (err) {
    console.warn('cleanupOldWebDocDrafts failed', err)
    return 0
  }
}

/** Reset cached DB handle (test helper). */
export function _resetWebDocDraftDBForTesting(): void {
  if (dbPromise) {
    dbPromise.then((db) => db.close()).catch(() => {})
  }
  dbPromise = null
}
